import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Typography,
    Button,
    Grid,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    SwapHoriz as SwapIcon,
    Clear as ClearIcon,
    History as HistoryIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import {
    hexToDecimal,
    decimalToHex,
    formatTokenAmount,
    hexToFormattedToken,
    addCommasSeparators,
    formatLargeNumber,
    smartConvert,
    copyToClipboard,
    isValidHex,
    isValidDecimal,
    formatBytes32,
    ConversionResult
} from '../../utils/hexConverter';
import logger from '../../utils/logger';

interface ConversionHistory {
    id: string;
    input: string;
    result: ConversionResult;
    timestamp: Date;
}

const StandaloneConverter: React.FC = () => {
    const [inputValue, setInputValue] = useState('');
    const [conversionMode, setConversionMode] = useState<'auto' | 'hex-to-decimal' | 'decimal-to-hex' | 'token'>('auto');
    const [tokenDecimals, setTokenDecimals] = useState(18);
    const [tokenSymbol, setTokenSymbol] = useState('');
    const [result, setResult] = useState<ConversionResult | null>(null);
    const [error, setError] = useState<string>('');
    const [copySuccess, setCopySuccess] = useState<string>('');
    const [history, setHistory] = useState<ConversionHistory[]>([]);
    const [showHistory, setShowHistory] = useState(false);

    // Load history from localStorage on mount
    useEffect(() => {
        try {
            const savedHistory = localStorage.getItem('hexConverterHistory');
            if (savedHistory) {
                const parsed = JSON.parse(savedHistory);
                setHistory(parsed.map((item: any) => ({
                    ...item,
                    timestamp: new Date(item.timestamp)
                })));
            }
        } catch (error) {
            logger.error('Failed to load conversion history:', error);
        }
    }, []);

    // Save history to localStorage
    const saveHistory = (newHistory: ConversionHistory[]) => {
        try {
            localStorage.setItem('hexConverterHistory', JSON.stringify(newHistory));
        } catch (error) {
            logger.error('Failed to save conversion history:', error);
        }
    };

    // Add to history
    const addToHistory = (input: string, result: ConversionResult) => {
        const newEntry: ConversionHistory = {
            id: Date.now().toString(),
            input,
            result,
            timestamp: new Date()
        };

        const newHistory = [newEntry, ...history.slice(0, 19)]; // Keep last 20 entries
        setHistory(newHistory);
        saveHistory(newHistory);
    };

    // Clear history
    const clearHistory = () => {
        setHistory([]);
        localStorage.removeItem('hexConverterHistory');
    };

    // Handle conversion
    const handleConvert = () => {
        if (!inputValue.trim()) {
            setError('Please enter a value to convert');
            setResult(null);
            return;
        }

        setError('');
        setCopySuccess('');

        try {
            let conversionResult: ConversionResult;

            switch (conversionMode) {
                case 'auto':
                    conversionResult = smartConvert(inputValue);
                    break;

                case 'hex-to-decimal':
                    if (!isValidHex(inputValue)) {
                        throw new Error('Invalid hex format');
                    }
                    const decimal = hexToDecimal(inputValue);
                    conversionResult = {
                        original: inputValue,
                        decimal,
                        formatted: addCommasSeparators(decimal),
                        type: 'hex',
                        confidence: 1,
                        suggestions: ['Manual hex to decimal conversion']
                    };
                    break;

                case 'decimal-to-hex':
                    if (!isValidDecimal(inputValue)) {
                        throw new Error('Invalid decimal format');
                    }
                    const hex = decimalToHex(inputValue);
                    conversionResult = {
                        original: inputValue,
                        decimal: inputValue,
                        formatted: hex,
                        type: 'hex',
                        confidence: 1,
                        suggestions: ['Manual decimal to hex conversion']
                    };
                    break;

                case 'token':
                    if (!isValidHex(inputValue)) {
                        throw new Error('Invalid hex format for token conversion');
                    }
                    const tokenFormatted = hexToFormattedToken(inputValue, tokenDecimals, tokenSymbol || undefined);
                    const tokenDecimal = hexToDecimal(inputValue);
                    conversionResult = {
                        original: inputValue,
                        decimal: tokenDecimal,
                        formatted: tokenFormatted,
                        type: 'amount',
                        confidence: 1,
                        suggestions: [`Formatted as ${tokenDecimals}-decimal token`]
                    };
                    break;

                default:
                    throw new Error('Unknown conversion mode');
            }

            setResult(conversionResult);
            addToHistory(inputValue, conversionResult);

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Conversion failed';
            setError(errorMessage);
            setResult(null);
        }
    };

    // Handle copy to clipboard
    const handleCopy = async (text: string, label: string) => {
        const success = await copyToClipboard(text);
        if (success) {
            setCopySuccess(`${label} copied to clipboard!`);
            setTimeout(() => setCopySuccess(''), 2000);
        } else {
            setError('Failed to copy to clipboard');
        }
    };

    // Clear all fields
    const handleClear = () => {
        setInputValue('');
        setResult(null);
        setError('');
        setCopySuccess('');
    };

    // Listen for tab switching events to pre-populate values
    useEffect(() => {
        const handleTabSwitch = (event: CustomEvent) => {
            const { value, mode } = event.detail;
            setInputValue(value);
            setConversionMode(mode);
            // Auto-convert after a short delay
            setTimeout(() => handleConvert(), 100);
        };

        window.addEventListener('switchToConverterTab', handleTabSwitch as EventListener);

        return () => {
            window.removeEventListener('switchToConverterTab', handleTabSwitch as EventListener);
        };
    }, []);

    // Load from history
    const loadFromHistory = (historyItem: ConversionHistory) => {
        setInputValue(historyItem.input);
        setResult(historyItem.result);
        setError('');
        setCopySuccess('');
    };

    // Handle Enter key
    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleConvert();
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Hex/Decimal Converter
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Convert between hexadecimal and decimal formats with smart detection and token formatting.
            </Typography>

            <Grid container spacing={3}>
                {/* Input Section */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Input
                            </Typography>

                            <TextField
                                fullWidth
                                label="Enter hex or decimal value"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="0x1e18 or 7704"
                                sx={{ mb: 2 }}
                                multiline
                                rows={2}
                            />

                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <FormControl sx={{ minWidth: 150 }}>
                                    <InputLabel>Conversion Mode</InputLabel>
                                    <Select
                                        value={conversionMode}
                                        onChange={(e) => setConversionMode(e.target.value as any)}
                                        label="Conversion Mode"
                                    >
                                        <MenuItem value="auto">Auto Detect</MenuItem>
                                        <MenuItem value="hex-to-decimal">Hex → Decimal</MenuItem>
                                        <MenuItem value="decimal-to-hex">Decimal → Hex</MenuItem>
                                        <MenuItem value="token">Token Format</MenuItem>
                                    </Select>
                                </FormControl>

                                {conversionMode === 'token' && (
                                    <>
                                        <TextField
                                            label="Decimals"
                                            type="number"
                                            value={tokenDecimals}
                                            onChange={(e) => setTokenDecimals(parseInt(e.target.value) || 18)}
                                            sx={{ width: 100 }}
                                            inputProps={{ min: 0, max: 30 }}
                                        />
                                        <TextField
                                            label="Symbol"
                                            value={tokenSymbol}
                                            onChange={(e) => setTokenSymbol(e.target.value)}
                                            sx={{ width: 120 }}
                                            placeholder="ETH"
                                        />
                                    </>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleConvert}
                                    startIcon={<SwapIcon />}
                                >
                                    Convert
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleClear}
                                    startIcon={<ClearIcon />}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowHistory(!showHistory)}
                                    startIcon={<HistoryIcon />}
                                >
                                    History ({history.length})
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Results Section */}
                    {(result || error) && (
                        <Card sx={{ mt: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Results
                                </Typography>

                                {error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {error}
                                    </Alert>
                                )}

                                {copySuccess && (
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        {copySuccess}
                                    </Alert>
                                )}

                                {result && (
                                    <Box>
                                        {/* Type Detection */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Chip
                                                label={result.type.toUpperCase()}
                                                color={result.confidence > 0.7 ? 'success' : result.confidence > 0.4 ? 'warning' : 'default'}
                                                size="small"
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                Confidence: {Math.round(result.confidence * 100)}%
                                            </Typography>
                                        </Box>

                                        {/* Original Value */}
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Original:
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                    {result.original}
                                                </Typography>
                                                <Tooltip title="Copy original">
                                                    <IconButton size="small" onClick={() => handleCopy(result.original, 'Original')}>
                                                        <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        {/* Decimal Value */}
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Decimal:
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                    {result.decimal}
                                                </Typography>
                                                <Tooltip title="Copy decimal">
                                                    <IconButton size="small" onClick={() => handleCopy(result.decimal, 'Decimal')}>
                                                        <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        {/* Formatted Value */}
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Formatted:
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                    {result.formatted}
                                                </Typography>
                                                <Tooltip title="Copy formatted">
                                                    <IconButton size="small" onClick={() => handleCopy(result.formatted, 'Formatted')}>
                                                        <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        {/* Additional Formats */}
                                        {result.type === 'amount' && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Additional Formats:
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" sx={{ minWidth: 100 }}>
                                                            Large format:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                            {formatLargeNumber(result.decimal)}
                                                        </Typography>
                                                        <Tooltip title="Copy large format">
                                                            <IconButton size="small" onClick={() => handleCopy(formatLargeNumber(result.decimal), 'Large format')}>
                                                                <CopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Suggestions */}
                                        {result.suggestions.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    <InfoIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                    Suggestions:
                                                </Typography>
                                                <List dense>
                                                    {result.suggestions.map((suggestion, index) => (
                                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                                            <ListItemText
                                                                primary={suggestion}
                                                                primaryTypographyProps={{ variant: 'body2' }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Grid>

                {/* History Section */}
                <Grid item xs={12} md={4}>
                    {showHistory && (
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">
                                        Conversion History
                                    </Typography>
                                    {history.length > 0 && (
                                        <Button size="small" onClick={clearHistory}>
                                            Clear All
                                        </Button>
                                    )}
                                </Box>

                                {history.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No conversion history yet.
                                    </Typography>
                                ) : (
                                    <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                                        {history.map((item) => (
                                            <React.Fragment key={item.id}>
                                                <ListItem
                                                    onClick={() => loadFromHistory(item)}
                                                    sx={{ px: 0, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                                {item.input.length > 20 ? `${item.input.substring(0, 20)}...` : item.input}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {item.result.type} → {item.result.formatted.length > 15 ? `${item.result.formatted.substring(0, 15)}...` : item.result.formatted}
                                                                </Typography>
                                                                <br />
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {item.timestamp.toLocaleTimeString()}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                                <Divider />
                                            </React.Fragment>
                                        ))}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default StandaloneConverter;
